import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import "./SignIn.css";
import config from "../config";
import { motion } from "framer-motion";
import Globe from "react-globe.gl";
import CountUp from "react-countup";
import { FiMail, FiLock, FiShield, FiBox, FiActivity, FiBarChart2, FiClipboard, FiRefreshCw, FiUsers, FiArrowRight, FiEye, FiEyeOff } from "react-icons/fi";
import { FaMicrosoft } from "react-icons/fa";

// Success Animation Component
const SuccessAnimation = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    className="success-animation"
  >
    <div className="success-checkmark">
      <div className="check-icon">
        <span className="icon-line line-tip"></span>
        <span className="icon-line line-long"></span>
        <div className="icon-circle"></div>
        <div className="icon-fix"></div>
      </div>
    </div>
    <div className="success-text">Authentication Successful!</div>
    <div className="success-subtext">Redirecting to dashboard...</div>
  </motion.div>
);

// 2FA Code Input Component
const TwoFAForm = ({ email, onSubmit, onCancel, loading, error }) => {
  const [code, setCode] = useState("");
  const inputsRef = useRef([]);
  const [timer, setTimer] = useState(60);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleChange = (index, e) => {
    const newValue = e.target.value;
    if (newValue.length <= 1 && /^\d*$/.test(newValue)) {
      const newCode = code.split('');
      newCode[index] = newValue;
      const updatedCode = newCode.join('');
      setCode(updatedCode);

      if (newValue && index < 5) {
        inputsRef.current[index + 1]?.focus();
      }

      if (!newValue && index > 0) {
        inputsRef.current[index - 1]?.focus();
      }

      // Auto-submit when 6 digits entered
      if (updatedCode.length === 6) {
        onSubmit(updatedCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pasteData)) {
      setCode(pasteData);
      onSubmit(pasteData);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="twofa-container"
    >
      <div className="twofa-header">
        <div className="twofa-icon-wrapper">
          <FiShield className="twofa-icon" />
        </div>
        <h2>Two-Factor Authentication</h2>
        <p className="twofa-subtitle">Enter verification code from your authenticator app</p>
      </div>

      <div className="user-email-display">
        <FiMail className="email-icon" />
        <span className="email-text">{email}</span>
      </div>

      <div className="code-input-section">
        <label className="code-label">Authenticator Code</label>

        <div className="code-inputs" onPaste={handlePaste}>
          {Array.from({ length: 6 }).map((_, index) => (
            <input
              key={index}
              ref={(el) => (inputsRef.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength="1"
              value={code[index] || ''}
              onChange={(e) => handleChange(index, e)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              disabled={loading}
              className={`code-digit ${code[index] ? 'filled' : ''} ${error ? 'error' : ''}`}
              autoFocus={index === 0 && !code}
            />
          ))}
        </div>

        <div className="code-progress">
          <div
            className="code-progress-fill"
            style={{ width: `${(code.length / 6) * 100}%` }}
          />
        </div>

        <p className="code-hint">
          Enter the 6-digit code from Microsoft Authenticator
        </p>

        {error && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="code-error-message">
            <span className="error-icon">!</span>
            {error}
          </motion.div>
        )}
      </div>

      <div className="timer-section">
        <div className="timer">
          <span className="timer-icon">⏱️</span>
          Code refreshes in: <span className="timer-value">{timer}s</span>
        </div>
      </div>

      <div className="twofa-actions">
        <button
          className="verify-btn"
          onClick={() => onSubmit(code)}
          disabled={loading || code.length !== 6}
        >
          {loading ? (
            <>
              <span className="spinner"></span>
              Verifying...
            </>
          ) : (
            'Verify & Continue'
          )}
        </button>

        <button
          type="button"
          className="cancel-2fa-btn"
          onClick={onCancel}
          disabled={loading}
        >
          ← Back to Sign In
        </button>
      </div>
    </motion.div>
  );
};

const SignIn = ({ setToken, setUserName, setPermissions }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [twoFARequired, setTwoFARequired] = useState(false);
  const [authData, setAuthData] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isGlobeLoaded, setIsGlobeLoaded] = useState(false);
  const navigate = useNavigate();
  const globeRef = useRef();

  useEffect(() => {
    const savedToken = Cookies.get("token");
    const savedUserName = Cookies.get("userName");
    const savedPermissions = Cookies.get("permissions");

    if (savedToken && savedUserName && savedPermissions) {
      try {
        const parsedPermissions = JSON.parse(savedPermissions);
        setToken(savedToken);
        setUserName(savedUserName);
        setPermissions(parsedPermissions);
        navigate("/");
      } catch (err) {
        Cookies.remove("token");
        Cookies.remove("userName");
        Cookies.remove("permissions");
      }
    }
  }, [setToken, setUserName, setPermissions, navigate]);

  // Handle Microsoft OAuth callback
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token");
    const userName = searchParams.get("userName");
    const permissions = searchParams.get("permissions");
    const error = searchParams.get("error");
    const cookieOptions = {
      expires: 7,
      path: '/',
      sameSite: 'Lax',
      ...(window.location.protocol === 'https:' ? { secure: true } : {})
    };

    if (error) {
      setError(`Authentication failed: ${error}`);
      // Clear the URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (token && userName && permissions) {
      try {
        const parsedPermissions = JSON.parse(permissions);
        
        // Show success animation
        setSuccess(true);
        setError("");

        // Wait for animation to complete
        setTimeout(() => {
          // Store in cookies
          Cookies.set("token", token, cookieOptions);
          Cookies.set("userName", userName, cookieOptions);
          Cookies.set("permissions", JSON.stringify(parsedPermissions), cookieOptions);
          window.localStorage.setItem("token", token);
          window.localStorage.setItem("userName", userName);
          window.localStorage.setItem("permissions", JSON.stringify(parsedPermissions));

          // Update parent state
          setUserName(userName);
          setPermissions(parsedPermissions);
          setToken(token);

          // Navigate to dashboard
          navigate("/");
        }, 1500);

        // Clear the URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (err) {
        setError("Failed to process login response");
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [setToken, setUserName, setPermissions, navigate]);

  useEffect(() => {
    if (globeRef.current) {
      globeRef.current.controls().autoRotate = true;
      globeRef.current.controls().autoRotateSpeed = 0.8;
      globeRef.current.controls().enableZoom = false;
      globeRef.current.pointOfView({ lat: 20.5937, lng: 78.9629, altitude: 2 }, 2000);
    }
  }, []);

  const handleEmailPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/api/auth/login`,
        { email, password }
      );

      // If backend says 2FA is required
      if (response.data && response.data.twoFARequired) {
        setAuthData({
          email,
          password,
          // Store any temporary token if provided
          tempToken: response.data.tempToken || null
        });
        setTwoFARequired(true);
        setError("");
      } else {
        // If no 2FA required, complete login
        completeLogin(response.data);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Invalid email or password");
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (code) => {
    if (code.length !== 6) {
      setError("Please enter a complete 6-digit code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Send both credentials and TOTP code together
      const response = await axios.post(
        `${config.apiBaseUrl}/api/auth/login`,
        {
          email: authData.email,
          password: authData.password,
          totp: code
        }
      );

      // Handle successful login
      completeLogin(response.data);
    } catch (err) {
      console.error("2FA Error:", err.response?.data);

      if (err.response?.status === 401) {
        setError("Invalid verification code. Please try again.");
      } else if (err.response?.status === 400) {
        setError(err.response.data.message || "Invalid code");
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Verification failed. Please try again.");
      }
      setLoading(false);
    }
  };

  const completeLogin = (data) => {
    const { token, user, userId: responseUserId } = data;
    const cookieOptions = {
      expires: 7,
      path: '/',
      sameSite: 'Lax',
      ...(window.location.protocol === 'https:' ? { secure: true } : {})
    };

    // Extract user info from response
    const userName = user?.name || data.name;
    const userId = user?._id || user?.id || responseUserId || data.userId;
    const permissions = user?.permissions || data.permissions;

    if (!token || !userName || !permissions) {
      setError("Invalid response from server");
      setLoading(false);
      return;
    }

    // Show success animation
    setSuccess(true);
    setError("");

    // Wait for animation to complete
    setTimeout(() => {
      // Store in cookies
      Cookies.set("token", token, cookieOptions);
      Cookies.set("userName", userName, cookieOptions);
      if (userId) Cookies.set("userId", userId, cookieOptions);
      Cookies.set("permissions", JSON.stringify(permissions), cookieOptions);
      window.localStorage.setItem("token", token);
      window.localStorage.setItem("userName", userName);
      window.localStorage.setItem("permissions", JSON.stringify(permissions));

      // Update parent state
      setUserName(userName);
      setPermissions(permissions);
      setToken(token);

      // Navigate to dashboard
      navigate("/");
    }, 1500);
  };

  const reset2FA = () => {
    setTwoFARequired(false);
    setAuthData(null);
    setError("");
    setPassword("");
  };

  const arcsData = [
    { startLat: 20.5937, startLng: 78.9629, endLat: 37.7749, endLng: -122.4194, color: ['#22c55e', '#10b981'] },
    { startLat: 20.5937, startLng: 78.9629, endLat: 51.5074, endLng: -0.1278, color: ['#22c55e', '#f59e0b'] },
    { startLat: 20.5937, startLng: 78.9629, endLat: -33.8688, endLng: 151.2093, color: ['#22c55e', '#3b82f6'] },
    { startLat: 20.5937, startLng: 78.9629, endLat: 35.6762, endLng: 139.6503, color: ['#22c55e', '#8b5cf6'] },
    { startLat: 20.5937, startLng: 78.9629, endLat: -23.5505, endLng: -46.6333, color: ['#22c55e', '#ec4899'] },
    { startLat: 20.5937, startLng: 78.9629, endLat: -30.5595, endLng: 22.9375, color: ['#22c55e', '#14b8a6'] },
  ];

  const ringsData = [
    { lat: 20.5937, lng: 78.9629, maxR: 15, propagationSpeed: 2, repeatPeriod: 1000 }
  ];

  const features = [
    { icon: <FiBox />, text: "Packing & Delivery Validation" },
    { icon: <FiActivity />, text: "Real-time Packing Tracking" },
    { icon: <FiBarChart2 />, text: "Performance Analytics" },
    { icon: <FiClipboard />, text: "Reporting Dashboard" },
    { icon: <FiRefreshCw />, text: "Odoo Integration" },
    { icon: <FiUsers />, text: "Role Based Access" },
  ];

  return (
    <div className="signin-wrapper">
      {/* LEFT: BRANDING AND FORM */}
      <motion.div
        className="signin-container"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="left-background-effects">
          <div className="left-mesh-gradient"></div>
          <div className="left-particles">
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className={`left-particle lp-${i}`}></div>
            ))}
          </div>
          <div className="left-stars"></div>
          <div className="left-waves"></div>
          <div className="left-glow-blob green-blob"></div>
          <div className="left-glow-blob blue-blob"></div>
          <div className="left-light-beam"></div>
        </div>

        <div className="left-brand-panel">
          <div className="brand-logo-container">
            <div className="logo-glow-ring"></div>
            <img
              src="/TGH%20New%20LOG0.webp"
              alt="The Good Health"
              className="brand-logo-img"
            />
          </div>
          <p className="brand-eyebrow">INTERNAL OPERATIONS</p>
          <h1 className="brand-heading">PACKING CONSOLE</h1>
          <p className="brand-statement">Smarter packing. Faster operations. Better accuracy.</p>
          <p className="brand-description">
            Manage packing, order validation, inventory flow, and delivery operations from one trusted workspace.
          </p>
        </div>

        <motion.div
          className="signin-card glass-card"
        >
          {success ? (
            <SuccessAnimation />
          ) : twoFARequired ? (
            <TwoFAForm
              email={authData?.email || email}
              onSubmit={handle2FASubmit}
              onCancel={reset2FA}
              loading={loading}
              error={error}
            />
          ) : (
            <>
                        

              {error && (
                <motion.div
                  className="error-box"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <span className="error-icon">!</span>
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleEmailPasswordSubmit} className="signin-form">
                <div className="input-group">
                  <label className="input-label">EMAIL</label>
                  <div className="input-wrapper">
                    <FiMail className="input-icon" />
                    <input
                      type="email"
                      placeholder="Test@a2znaturopathy.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      required
                    />
                    <div className="input-glow"></div>
                  </div>
                </div>

                <div className="input-group">
                  <label className="input-label">PASSWORD</label>
                  <div className="input-wrapper">
                    <FiLock className="input-icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                      className="toggle-password-btn"
                      tabIndex="-1"
                    >
                      {showPassword ? (
                        <FiEyeOff className="input-icon-right" />
                      ) : (
                        <FiEye className="input-icon-right" />
                      )}
                    </button>
                    <div className="input-glow"></div>
                  </div>
                </div>

                <motion.button
                  className={`signin-btn ${loading ? 'loading' : ''}`}
                  disabled={loading}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      Signing in...
                    </>
                  ) : (
                    <>Sign In <FiArrowRight className="btn-arrow" /></>
                  )}
                </motion.button>
              </form>

              <div className="signin-divider">
                <span>OR</span>
              </div>

              <motion.button
                className="microsoft-signin-btn"
                onClick={() => {
                  setLoading(true);
                  window.location.href = `${config.apiBaseUrl}/api/auth/microsoft-login`;
                }}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                disabled={loading}
              >
                <FaMicrosoft className="microsoft-icon" aria-hidden="true" />
                Sign in with Microsoft
              </motion.button>

              <div className="signin-footer">
                <div className="secure-badge floating-badge">
                  <span className="pulse-dot-green">🟢</span>
                  Protected by Microsoft Authenticator
                </div>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>

      {/* RIGHT: ANIMATED WORLD VISUALIZATION */}
      <div className="signin-right">
        {/* Background Effects */}
        <div className="dark-bg-gradient"></div>
        <div className="mesh-gradient"></div>
        <div className="tiny-grid-texture"></div>
        <div className="light-rays"></div>

        {/* Particles */}
        <div className="particles-container">
          {Array.from({ length: 20 }).map((_, i) => (
            <div key={i} className={`particle particle-${i}`}></div>
          ))}
        </div>

        <motion.div
          className="globe-container"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: isGlobeLoaded ? 1 : 0, scale: isGlobeLoaded ? 1 : 0.8 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          style={{ visibility: isGlobeLoaded ? "visible" : "hidden" }}
        >
          <div className="globe-glow"></div>
          <Globe
            ref={globeRef}
            onGlobeReady={() => setIsGlobeLoaded(true)}
            globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
            bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
            backgroundColor="rgba(0,0,0,0)"
            atmosphereColor="#22c55e"
            atmosphereAltitude={0.15}
            arcsData={arcsData}
            arcColor="color"
            arcDashLength={0.4}
            arcDashGap={0.2}
            arcDashAnimateTime={2000}
            ringsData={ringsData}
            ringColor={() => '#22c55e'}
            ringMaxRadius="maxR"
            ringPropagationSpeed="propagationSpeed"
            ringRepeatPeriod="repeatPeriod"
            width={800}
            height={800}
          />
        </motion.div>

        {/* Feature Cards Floating */}
        <div className="feature-cards-wrapper">
          {features.map((f, i) => (
            <motion.div
              key={i}
              className={`floating-feature-card card-${i}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 + i * 0.1, duration: 0.8 }}
              whileHover={{ scale: 1.05, y: -5 }}
            >
              <div className="feature-icon">{f.icon}</div>
              <div className="feature-text">{f.text}</div>
            </motion.div>
          ))}
        </div>

        {/* BOTTOM STATS */}
        <motion.div
          className="bottom-stats-container"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
        >

          <div className="stat-card">
            <h4><CountUp end={99.9} decimals={1} duration={2.5} suffix="%" /></h4>
            <p>System Uptime</p>
          </div>
          <div className="stat-card">
            <h4>24/7</h4>
            <p>Support</p>
          </div>
          <div className="stat-card">
            <h4><CountUp end={500} duration={2.5} suffix="K+" /></h4>
            <p>Orders Processed</p>
          </div>
          <div className="stat-card">
            <h4 className="pulse-text">Live</h4>
            <p>Real-time Monitoring</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SignIn;